/**
 * What the content area shows when a tab cannot render at all (audit C3). Two things get
 * here: a tab's chunk that no longer exists because the portal was deployed while this tab
 * was open (the dynamic import 404s and `Suspense` has nothing to catch it with), and a
 * render error in a tab. Both used to leave a blank page under the heading. Now a grey group
 * says what happened and offers « Recharger »; the session is a cookie and the CSRF token
 * lives in `sessionStorage`, so the reload lands back signed in. Another tab clears it.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

/** How browsers report a dynamic import whose file is gone (Chrome, Firefox, Safari, Vite). */
const CHUNK_GONE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (?:CSS )?chunk/iu;

export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_GONE.test(error.message);
}

interface Props {
  readonly children: ReactNode;
  /** Changes when the creator opens another tab: the next tab gets a fresh try. */
  readonly resetKey: string;
  readonly t: (key: string) => string;
}

interface State {
  readonly error: unknown;
  readonly caught: boolean;
  readonly resetKey: string;
}

export class Boundary extends Component<Props, State> {
  override state: State = { error: null, caught: false, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error, caught: true };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.resetKey)
      return { error: null, caught: false, resetKey: props.resetKey };
    return null;
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Nothing is sent anywhere: the portal has no telemetry (no capital, no third party).
    console.error('[partner-portal] page failed to render', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.caught) return this.props.children;
    const { t } = this.props;
    const { error } = this.state;
    const updated = isChunkLoadError(error);
    return (
      <div className="group boundary" role="alert">
        <p>{updated ? t('boundaryUpdated') : t('boundaryFailed')}</p>
        <p>{t('boundaryKeep')}</p>
        {updated ? null : (
          <p className="caption">{error instanceof Error ? error.message : String(error)}</p>
        )}
        <button type="button" className="primary" onClick={() => window.location.reload()}>
          {t('reload')}
        </button>
      </div>
    );
  }
}
